import React, { useState, useEffect } from "react";
import { marked } from "marked";
import DOMPurify from "dompurify";

const PROMPT_MODULES = import.meta.glob(["../../../../../prompts/**/*.md"], { query: "?raw", import: "default" });

const SKILL_MODULES = import.meta.glob(["../../../../../skills/**/*.md"], { query: "?raw", import: "default" });

const LIBRARIES = {
	prompts: {
		title: "Prompts",
		subtitle: "Prompt libraries, generation rules, templates, and output contracts",
		rootLabel: "prompts",
		modules: PROMPT_MODULES
	},
	skills: {
		title: "Skills",
		subtitle: "Agent skill instructions, references, and reusable operating playbooks",
		rootLabel: "skills",
		modules: SKILL_MODULES
	}
};

const INSTALL_NOTES = {
	prompts: {
		title: "Prompt Library Setup",
		auth: "",
		chatgptWeb: [
			"Attach or paste the selected markdown into a ChatGPT project, custom GPT, or conversation.",
			"For MCP-backed prompts, enable the matching connector from the MCP Tools page.",
			"Keep generated writes as drafts unless AOP creates a pending action."
		],
		claudeApp: [
			"Add prompt markdown files to a Claude Project or paste the selected prompt into the chat.",
			"Use the connected MCP servers for live Salesforce/CSD/Git context instead of copying records by hand.",
			"Keep client-sensitive prompt packs in the approved workspace."
		],
		cli: "Use selected files by path from the repo, for example: prompts/onsite/_instructions.md or prompts/email/operating-instructions.md.",
		references: ["prompts/**/*.md"]
	},
	skills: {
		title: "Skill Installation Setup",
		auth: "AOP loads local skills from this repo. Each agent run records the git commit for the loaded SKILL.md.",
		chatgptWeb: [
			"Attach SKILL.md and relevant references to a ChatGPT project/custom GPT as knowledge.",
			"Enable matching MCP connectors from the MCP Tools page before running live lookup workflows.",
			"Do not paste secrets into skill text; use connector auth or environment variables."
		],
		claudeApp: [
			"Add SKILL.md and references to a Claude Project or app workspace knowledge area.",
			"For Claude Desktop local MCPs, add matching servers with the commands shown on MCP Tools.",
			"Use the skill source path in the task prompt when multiple skills could apply."
		],
		cli: "Run from the repo root and reference the skill path, for example: skills/campaign-dev/SKILL.md. AOP worker reads skills/*/SKILL.md directly."
		// dependencies: [
		//   ['PaddleOCR', 'https://github.com/PaddlePaddle/PaddleOCR', 'Optional OCR layer for reading screenshots, PDFs, and image-heavy client assets.'],
		//   ['tree-sitter', 'https://tree-sitter.github.io/tree-sitter/', 'Optional parser layer for code-aware review, prompt linting, and generated file analysis.'],
		// ],
		// references: ['skills/*/SKILL.md', 'skills/*/references/*.md'],
	}
};

/* ── path parsing ─────────────────────────────────────────── */
function parsePaths(modules, rootLabel) {
	const tree = {};
	for (const rawPath of Object.keys(modules)) {
		const match = rawPath.match(new RegExp(`\\/(${rootLabel})\\/`));
		if (!match) continue;
		const idx = rawPath.indexOf(match[0]);
		const rel = rawPath.slice(idx + 1);
		const parts = rel.split("/");
		const section = parts[0];
		const rest = parts.slice(1);

		if (!tree[section]) tree[section] = {};

		if (rest.length === 1) {
			// file at section root
			if (!tree[section][""]) tree[section][""] = [];
			tree[section][""].push({ name: rest[0], path: rawPath });
		} else {
			const folder = rest.slice(0, -1).join("/");
			const file = rest[rest.length - 1];
			if (!tree[section][folder]) tree[section][folder] = [];
			tree[section][folder].push({ name: file, path: rawPath });
		}
	}

	return Object.fromEntries(
		Object.entries(tree)
			.sort(([a], [b]) => a.localeCompare(b))
			.map(([section, folders]) => [
				section,
				Object.fromEntries(
					Object.entries(folders)
						.sort(([a], [b]) => a.localeCompare(b))
						.map(([folder, files]) => [folder, [...files].sort((a, b) => a.name.localeCompare(b.name))])
				)
			])
	);
}

Object.values(LIBRARIES).forEach((config) => {
	config.tree = parsePaths(config.modules, config.rootLabel);
});

function firstPath(tree) {
	for (const folders of Object.values(tree)) {
		for (const files of Object.values(folders)) {
			if (files[0]) return files[0].path;
		}
	}
	return null;
}

function prettyName(filename) {
	if (filename === "SKILL.md") return "Skill Instructions";
	if (filename === "_README.md") return "Readme";
	if (filename === "_instructions.md") return "Instructions";
	return filename
		.replace(/\.md$/, "")
		.replace(/^\d+-/, "")
		.replace(/[-_]/g, " ")
		.replace(/\b\w/g, (c) => c.toUpperCase());
}

function prettyFolder(folder) {
	if (!folder) return "";
	return folder
		.replace(/\//g, " / ")
		.replace(/[-_]/g, " ")
		.replace(/\b\w/g, (c) => c.toUpperCase());
}

function downloadFilename(selectedPath) {
	if (!selectedPath) return "document.md";
	const relative = selectedPath.replace(/.*\/(prompts|skills)\//, "$1/");
	return relative.replace(/[\/\\]+/g, "__");
}

function relativeDocPath(selectedPath) {
	if (!selectedPath) return "";
	return selectedPath.replace(/.*\/(prompts|skills)\//, "$1/");
}

function downloadTextFile(filename, text, mimeType = "text/markdown;charset=utf-8") {
	const blob = new Blob([text], { type: mimeType });
	const href = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = href;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	link.remove();
	window.URL.revokeObjectURL(href);
}

function sectionBundleFilename(library, section) {
	return `${library}-${section}-bundle.md`.replace(/[^a-zA-Z0-9._-]+/g, "-");
}

/* ── markdown renderer ────────────────────────────────────── */
marked.setOptions({ breaks: true, gfm: true });

function renderMd(raw) {
	return DOMPurify.sanitize(marked.parse(raw));
}

function SetupColumn({ title, content }) {
	const items = Array.isArray(content) ? content : [content];
	return (
		<div className="aop-docs-setup-column">
			<div className="aop-docs-setup-column-title">{title}</div>
			{items.length === 1 ? (
				<p>{items[0]}</p>
			) : (
				<ol>
					{items.map((item) => (
						<li key={item}>{item}</li>
					))}
				</ol>
			)}
		</div>
	);
}

function ReaderSetupGuide({ library }) {
	const guide = INSTALL_NOTES[library] || INSTALL_NOTES.prompts;
	return (
		<div className="aop-card aop-docs-setup-card">
			<div className="aop-card-header">
				<div>
					<span className="aop-card-title">{guide.title}</span>
					<div className="aop-card-subtitle">
						ChatGPT web, Claude app, and CLI usage for the selected markdown source
					</div>
				</div>
				<span className="aop-badge queued">Install notes</span>
			</div>
			<div className="aop-card-body">
				{guide.auth && (
					<div className="aop-docs-setup-auth">
						<span>Auth</span>
						<p>{guide.auth}</p>
					</div>
				)}
				<div className="aop-docs-setup-grid">
					<SetupColumn title="ChatGPT Web" content={guide.chatgptWeb} />
					<SetupColumn title="Claude App" content={guide.claudeApp} />
					<SetupColumn title="CLI / AOP Worker" content={guide.cli} />
				</div>
				{/* {guide.dependencies && (
          <div className="aop-docs-dependencies">
            {guide.dependencies.map(([label, href, description]) => (
              <a key={label} href={href} target="_blank" rel="noreferrer">
                <strong>{label}</strong>
                <span>{description}</span>
              </a>
            ))}
          </div>
        )}
        <div className="aop-docs-reference-row">
          {guide.references.map(ref => <code key={ref}>{ref}</code>)}
        </div> */}
			</div>
		</div>
	);
}

/* ── file tree sidebar ────────────────────────────────────── */
function FileTree({
	tree,
	onSelect,
	selected,
	query,
	onQueryChange,
	onDownloadSection,
	onDownloadFolder,
	allowSectionDownload
}) {
	const [open, setOpen] = useState(Object.fromEntries(Object.keys(tree).map((s) => [s, true])));
	const [folderOpen, setFolderOpen] = useState({});

	useEffect(() => {
		setOpen(Object.fromEntries(Object.keys(tree).map((s) => [s, true])));
		setFolderOpen({});
	}, [tree]);

	const toggleSection = (s) => setOpen((o) => ({ ...o, [s]: !o[s] }));
	const toggleFolder = (k) => setFolderOpen((o) => ({ ...o, [k]: !o[k] }));
	const normalizedQuery = query.trim().toLowerCase();

	return (
		<nav className="aop-docs-tree">
			<div className="aop-docs-search">
				<input
					className="aop-form-input"
					value={query}
					onChange={(e) => onQueryChange(e.target.value)}
					placeholder="Search files"
				/>
			</div>

			{Object.entries(tree).map(([section, folders]) => {
				const visibleFolders = Object.entries(folders)
					.map(([folder, files]) => {
						const visibleFiles = normalizedQuery
							? files.filter((file) =>
									`${section}/${folder}/${file.name}`.toLowerCase().includes(normalizedQuery)
							  )
							: files;
						return [folder, visibleFiles];
					})
					.filter(([, files]) => files.length > 0);

				if (visibleFolders.length === 0) return null;

				return (
					<div key={section} className="aop-docs-section">
						<div className="aop-docs-section-head" onClick={() => toggleSection(section)}>
							{/* <span className="aop-docs-section-arrow">{open[section] ? "▾" : "▸"}</span> */}
							<span>{section}</span>
							{allowSectionDownload && (
								<button
									className="aop-btn ghost"
									style={{ fontSize: 10, marginLeft: "auto", padding: "2px 8px" }}
									onClick={(event) => {
										event.stopPropagation();
										onDownloadSection(section);
									}}
								>
									Download All
								</button>
							)}
						</div>
						{open[section] &&
							visibleFolders.map(([folder, files]) => {
								const fkey = `${section}/${folder}`;
								const isOpen = folderOpen[fkey] !== false || normalizedQuery;
								return (
									<div key={fkey} className="aop-docs-folder-group">
										{folder && (
											<div className="aop-docs-folder-head" onClick={() => toggleFolder(fkey)}>
												{/* <span className="aop-docs-section-arrow" style={{ fontSize: 9 }}>
													{isOpen ? "▾" : "▸"}
												</span> */}
												<span>{prettyFolder(folder)}</span>
												{allowSectionDownload && (
													<button
														className="aop-btn ghost"
														style={{ fontSize: 10, marginLeft: "auto", padding: "2px 8px" }}
														onClick={(event) => {
															event.stopPropagation();
															onDownloadFolder(section, folder);
														}}
													>
														Download All
													</button>
												)}
											</div>
										)}
										{(isOpen || !folder) &&
											files.map((f) => (
												<div
													key={f.path}
													className={`aop-docs-file${selected === f.path ? " active" : ""}`}
													style={{ paddingLeft: folder ? 28 : 16 }}
													onClick={() => onSelect(f.path)}
												>
													{prettyName(f.name)}
												</div>
											))}
									</div>
								);
							})}
					</div>
				);
			})}
		</nav>
	);
}

/* ── main component ───────────────────────────────────────── */
export default function PromptDocs({ library = "prompts" }) {
	const config = LIBRARIES[library] || LIBRARIES.prompts;
	const [selectedPath, setSelectedPath] = useState(() => firstPath(config.tree));
	const [content, setContent] = useState(null);
	const [loading, setLoading] = useState(false);
	const [raw, setRaw] = useState(false);
	const [query, setQuery] = useState("");

	useEffect(() => {
		setSelectedPath(firstPath(config.tree));
		setContent(null);
		setRaw(false);
		setQuery("");
	}, [library]);

	useEffect(() => {
		if (!selectedPath || !config.modules[selectedPath]) {
			setContent(null);
			return;
		}
		let cancelled = false;
		setLoading(true);
		setContent(null);
		config.modules[selectedPath]().then((text) => {
			if (!cancelled) {
				setContent(text);
				setLoading(false);
			}
		});
		return () => {
			cancelled = true;
		};
	}, [selectedPath, library]);

	const title = selectedPath ? prettyName(selectedPath.split("/").pop()) : "";

	const downloadSelectedFile = () => {
		if (!selectedPath || !content) return;
		downloadTextFile(downloadFilename(selectedPath), content);
	};

	const downloadSection = async (section) => {
		const folders = config.tree[section];
		if (!folders) return;

		const files = Object.values(folders).flat();
		const bundleParts = await Promise.all(
			files.map(async (file) => {
				const text = await config.modules[file.path]();
				return `# ${relativeDocPath(file.path)}\n\n${text.trim()}\n`;
			})
		);

		downloadTextFile(sectionBundleFilename(library, section), bundleParts.join("\n---\n\n"));
	};

	const downloadFolder = async (section, folder) => {
		const files = config.tree[section]?.[folder];
		if (!files?.length) return;

		const bundleParts = await Promise.all(
			files.map(async (file) => {
				const text = await config.modules[file.path]();
				return `# ${relativeDocPath(file.path)}\n\n${text.trim()}\n`;
			})
		);

		downloadTextFile(
			sectionBundleFilename(library, `${section}-${folder || "root"}`),
			bundleParts.join("\n---\n\n")
		);
	};

	return (
		<div>
			<div className="aop-page-header">
				<div>
					<h1 className="aop-page-title">{config.title}</h1>
					<p className="aop-page-subtitle">{config.subtitle}</p>
				</div>
				<div className="aop-page-actions">
					<span className="aop-badge completed">{Object.keys(config.modules).length} files</span>
					<span className="aop-badge queued">Markdown reader</span>
				</div>
			</div>

			<ReaderSetupGuide library={library} />

			<div className="aop-docs-layout">
				<FileTree
					tree={config.tree}
					onSelect={setSelectedPath}
					selected={selectedPath}
					query={query}
					onQueryChange={setQuery}
					onDownloadSection={downloadSection}
					onDownloadFolder={downloadFolder}
					allowSectionDownload={library === "prompts"}
				/>

				<div className="aop-docs-viewer">
					{!selectedPath && (
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								height: "100%",
								color: "var(--text-dim)",
								gap: 12
							}}
						>
							<svg
								width="40"
								height="40"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.5"
								strokeLinecap="round"
								strokeLinejoin="round"
								opacity="0.4"
							>
								<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
								<polyline points="14 2 14 8 20 8" />
							</svg>
							<span style={{ fontSize: 14 }}>Select a file to view</span>
						</div>
					)}

					{loading && <div style={{ padding: 40, color: "var(--text-dim)", fontSize: 13 }}>Loading…</div>}

					{content && !loading && (
						<>
							<div className="aop-docs-viewer-header">
								<div>
									<div style={{ fontSize: 16, fontWeight: 700, color: "var(--text)" }}>{title}</div>
									<div
										style={{
											fontSize: 11,
											color: "var(--text-dim)",
											fontFamily: "monospace",
											marginTop: 2
										}}
									>
										{selectedPath ? relativeDocPath(selectedPath) : ""}
									</div>
								</div>
								<div style={{ display: "flex", gap: 8 }}>
									<button
										className="aop-btn ghost"
										style={{ fontSize: 10 }}
										onClick={downloadSelectedFile}
									>
										Download
									</button>
									<button
										className="aop-btn ghost"
										style={{ fontSize: 10 }}
										onClick={() => setRaw((r) => !r)}
									>
										{raw ? "Rendered" : "Raw"}
									</button>
								</div>
							</div>

							{raw ? (
								<pre className="aop-docs-raw">{content}</pre>
							) : (
								<div
									className="aop-docs-body"
									dangerouslySetInnerHTML={{ __html: renderMd(content) }}
								/>
							)}
						</>
					)}
				</div>
			</div>
		</div>
	);
}
